"use client";

import {
	RiCheckDoubleLine,
	RiCheckLine,
	RiCloseLine,
	RiNotificationFill,
	RiNotificationLine,
} from "@remixicon/react";
import { formatShortDateWithPreferences } from "@workspace/shared-utils";
import type { NotificationType } from "@workspace/types";
import { Badge } from "@workspace/ui/components/badge";
import { Spinner } from "@workspace/ui/components/spinner";
import { Button } from "@workspace/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { useCallback } from "react";
import { useSupabase } from "@/components/providers/supabase-auth-provider";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { useFollowActions } from "@/hooks/features/social/use-follow-actions";
import { useRealtimeNotifications } from "@/hooks/features/social/use-realtime-data";
import { trpc } from "@/lib/trpc/react";
import { NotificationAvatar } from "./notification-avatar";

export interface Notification {
	id: string;
	type: NotificationType;
	content: {
		followerId?: string;
		followerUsername?: string;
		followerName?: string;
		followerAvatar?: string;
		acceptedById?: string;
		acceptedByUsername?: string;
		acceptedByName?: string;
		acceptedByAvatar?: string;
		rejectedById?: string;
		rejectedByUsername?: string;
		rejectedByName?: string;
		rejectedByAvatar?: string;
		message: string;
	};
	read: boolean;
	created_at: string;
}

interface TopNavNotificationsProps {
	onViewAll?: () => void;
}

export function TopNavNotifications({ onViewAll }: TopNavNotificationsProps) {
	// Get user preferences for date formatting
	const userPreferences = useUserPreferences();
	const { user } = useSupabase();

	// tRPC utils no longer needed with real-time system

	// Use follow actions for accept/reject
	const { acceptFollowRequest, rejectFollowRequest } = useFollowActions();

	// Use real-time-first notifications - no cache invalidation needed!
	const {
		notifications: rawNotifications,
		unreadCount,
		removeNotification,
	} = useRealtimeNotifications(user?.id || null);

	// Filter out any notifications with missing required data
	const notifications = rawNotifications.filter((n) => {
		// Must have an ID and type
		if (!n.id || !n.type) {
			return false;
		}
		// For follow_accepted/follow_rejected, must have the user info
		if (
			(n.type === "follow_accepted" && !n.content?.acceptedById) ||
			(n.type === "follow_rejected" && !n.content?.rejectedById) ||
			(n.type === "follow_request" && !n.content?.followerId)
		) {
			return false;
		}
		return true;
	});

	// Loading state is managed by real-time hook
	const isLoading = !user;

	// tRPC mutations - simpler now with real-time handling state!
	const dismissNotificationsMutation =
		trpc.social.dismissNotifications.useMutation({
			onError: () => {
				toast({
					variant: "destructive",
					description: "Failed to dismiss notification",
				});
			},
			// No optimistic updates needed - real-time handles state changes!
		});

	// Dismiss all mutation - uncomment when UI button is added
	// const dismissAllMutation = trpc.social.dismissAllNotifications.useMutation({
	// 	onError: () => {
	// 		toast({
	// 			variant: "destructive",
	// 			description: "Failed to dismiss all notifications",
	// 		});
	// 	},
	// 	onSuccess: () => {
	// 		toast({ description: "All notifications dismissed" });
	// 	},
	// 	// No cache invalidation needed - real-time updates automatically!
	// });

	const markAsReadMutation = trpc.social.markNotificationsAsRead.useMutation();
	// Real-time will automatically update read status!

	// Handle follow request actions
	const handleFollowAction = async (
		requesterId: string,
		action: "accept" | "reject",
		notificationId: string,
	) => {
		try {
			// Optimistically remove the notification from the UI immediately
			removeNotification(notificationId);

			// Handle the follow request - server will automatically dismiss the notification
			if (action === "accept") {
				await acceptFollowRequest(requesterId);
			} else {
				await rejectFollowRequest(requesterId);
			}

			// Server has dismissed the notification in the transaction
			// Real-time will confirm the removal
		} catch (error) {
			// Error handled by mutations - notification will reappear if needed
			console.error("Error handling follow request:", error);
		}
	};

	// Dismiss all notifications - unused but keeping for future
	// const dismissAllNotifications = () => {
	// 	dismissAllMutation.mutate();
	// };

	// Dismiss individual notification - optimistic with real-time confirmation!
	const dismissNotification = (notificationId: string) => {
		// Optimistically remove the notification immediately
		removeNotification(notificationId);

		// Tell the server to dismiss it
		dismissNotificationsMutation.mutate({ notificationIds: [notificationId] });
		// Real-time will confirm the dismissal
	};

	// Mark notifications as read when dropdown opens
	const markNotificationsAsRead = useCallback(async () => {
		if (unreadCount === 0) return;

		const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);

		if (unreadIds.length === 0) return;

		// Mark as read using tRPC mutation
		markAsReadMutation.mutate({ notificationIds: unreadIds });
	}, [notifications, unreadCount, markAsReadMutation]);

	const formatTimeAgo = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffInMinutes = Math.floor(
			(now.getTime() - date.getTime()) / (1000 * 60),
		);

		if (diffInMinutes < 1) return "Just now";
		if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

		const diffInHours = Math.floor(diffInMinutes / 60);
		if (diffInHours < 24) return `${diffInHours}h ago`;

		const diffInDays = Math.floor(diffInHours / 24);
		if (diffInDays < 7) return `${diffInDays}d ago`;

		return formatShortDateWithPreferences(date, userPreferences);
	};

	const renderNotification = (notification: Notification) => {
		const { content, type } = notification;

		// Get the appropriate user info based on notification type
		const getUserInfo = () => {
			switch (type) {
				case "follow_request":
					return {
						id: content.followerId,
						name: content.followerName || content.followerUsername || "User",
						username: content.followerUsername,
						avatar: content.followerAvatar,
					};
				case "follow_accepted":
					return {
						id: content.acceptedById,
						name:
							content.acceptedByName || content.acceptedByUsername || "User",
						username: content.acceptedByUsername,
						avatar: content.acceptedByAvatar,
					};
				case "follow_rejected":
					return {
						id: content.rejectedById,
						name:
							content.rejectedByName || content.rejectedByUsername || "User",
						username: content.rejectedByUsername,
						avatar: content.rejectedByAvatar,
					};
				default:
					return { id: "", name: "User", username: null, avatar: null };
			}
		};

		const userInfo = getUserInfo();

		return (
			<div key={notification.id} className="group relative px-2">
				<Link
					href={`/${userInfo.username || userInfo.id}`}
					className="relative block rounded-lg px-3 py-2.5 transition-colors hover:bg-muted"
					onClick={(e) => {
						// Don't navigate if clicking on buttons
						if ((e.target as HTMLElement).closest("button")) {
							e.preventDefault();
						}
					}}
				>
					<div className="flex items-start gap-3">
						<NotificationAvatar
							userId={userInfo.id}
							username={userInfo.username}
							className="h-8 w-8 flex-shrink-0"
						/>

						<div className="min-w-0 flex-1">
							<div className="mb-1 flex items-center gap-2">
								<span className="truncate font-medium text-sm">
									@{userInfo.username || "user"}
								</span>
								{!notification.read && (
									<div className="h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
								)}
							</div>

							<p className="mb-1.5 text-muted-foreground text-sm">
								{type === "follow_request"
									? `@${userInfo.username || "user"} wants to follow you.`
									: `${content.message}${content.message.endsWith(".") ? "" : "."}`}
							</p>

							<div className="flex items-center justify-between">
								<span className="text-muted-foreground text-xs">
									{formatTimeAgo(notification.created_at)}
								</span>

								{(() => {
									if (type === "follow_request" && userInfo.id) {
										return dismissNotificationsMutation.isPending ? (
											<div className="flex h-6 items-center justify-center">
												<Spinner className="h-4 w-4 animate-spin text-muted-foreground" />
											</div>
										) : (
											<div className="flex gap-1">
												<TooltipProvider>
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="outline"
																size="sm"
																className="h-6 px-2 text-xs hover:border-destructive hover:text-destructive-11"
																onClick={() =>
																	handleFollowAction(
																		userInfo.id!,
																		"reject",
																		notification.id,
																	)
																}
																disabled={false}
															>
																<RiCloseLine className="h-3 w-3" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															<p>Reject</p>
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
												<TooltipProvider>
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="default"
																size="sm"
																className="h-6 px-2 text-xs"
																onClick={() =>
																	handleFollowAction(
																		userInfo.id!,
																		"accept",
																		notification.id,
																	)
																}
																disabled={false}
															>
																<RiCheckLine className="h-3 w-3" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															<p>Accept</p>
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											</div>
										);
									} else if (
										type === "follow_accepted" ||
										type === "follow_rejected"
									) {
										return (
											<TooltipProvider>
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="default"
															size="sm"
															className="h-6 px-2 text-xs"
															onClick={(e) => {
																e.stopPropagation();
																e.preventDefault();
																dismissNotification(notification.id);
															}}
															disabled={dismissNotificationsMutation.isPending}
														>
															<RiCheckDoubleLine className="h-3 w-3" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>
														<p>Dismiss</p>
													</TooltipContent>
												</Tooltip>
											</TooltipProvider>
										);
									}
									return null;
								})()}
							</div>
						</div>
					</div>
				</Link>
			</div>
		);
	};

	// Mark notifications as read when dropdown opens
	const handleDropdownOpenChange = useCallback(
		(open: boolean) => {
			if (open && markNotificationsAsRead && notifications.length > 0) {
				const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);

				if (unreadIds.length > 0) {
					markNotificationsAsRead();
				}
			}
		},
		[markNotificationsAsRead, notifications],
	);

	return (
		<DropdownMenu onOpenChange={handleDropdownOpenChange}>
			<DropdownMenuTrigger asChild>
				<button
					className={cn(
						"relative flex h-8 w-8 items-center justify-center rounded-full outline-none transition-all duration-200",
						"hover:ring-4 hover:ring-primary/10 active:scale-95 active:ring-primary/20",
						"focus-visible:ring-2 focus-visible:ring-primary/30",
					)}
					aria-label="Notifications"
				>
					{unreadCount > 0 ? (
						<RiNotificationFill className="h-4 w-4" />
					) : (
						<RiNotificationLine className="h-4 w-4" />
					)}

					{unreadCount > 0 && (
						<Badge
							className="-top-1 -right-1 absolute flex h-5 w-5 items-center justify-center rounded-full p-0 text-xs"
							variant="destructive"
						>
							{unreadCount > 9 ? "9+" : unreadCount}
						</Badge>
					)}
				</button>
			</DropdownMenuTrigger>

			<DropdownMenuContent
				align="end"
				sideOffset={8}
				className="w-80 rounded-2xl"
			>
				<div className="border-b px-3 py-2.5">
					<div className="flex items-center justify-between">
						<h3 className="font-semibold text-sm">Notifications</h3>
						{notifications.length > 0 && onViewAll && (
							<Button
								variant="ghost"
								size="sm"
								className="h-auto p-1 text-xs"
								onClick={onViewAll}
							>
								View all
							</Button>
						)}
					</div>
				</div>

				<div className="max-h-96 overflow-y-auto">
					{isLoading ? (
						<div className="flex items-center justify-center py-6">
							<Spinner className="h-5 w-5 animate-spin text-muted-foreground" />
						</div>
					) : notifications.length === 0 ? (
						<div className="py-6 text-center">
							<RiNotificationLine className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
							<p className="text-muted-foreground text-sm">
								No notifications yet
							</p>
						</div>
					) : (
						<div className="py-2 space-y-1">
							{notifications.map(renderNotification)}
						</div>
					)}
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
